import React, { useEffect, useState } from "react";
import ViewTable from "../../../Components/Common/ViewTable";
import { get, post, put, del, api } from "../../../utils/api";
import { formatDate, formatReportingDateGhgEntry } from "../../../utils/formatDate";
import Cards from "../../../Components/Common/Cards";
import FilterModal from "../../../Components/Common/FilterModal";
import ActionButtons from "../../../Components/Common/ActionButtons";
import ViewData from "./ViewData";
import Form from "./Form";
import { Alert, Snackbar } from "@mui/material";
import BulkUploadModal from "../../../Components/Common/BulkUploadModal";
import BulkUploadSkippedDialog from "../../../Components/Common/BulkUploadSkippedDialog";
import DeleteAlertDialog from "../../../Components/Masters/DeleteAlertDialog";
import BulkUploadReview from "./BulkUploadReview";
import { useUser } from "../../../context/AuthContext";

/** Bulk template columns only — skipped-entry Excel matches this. */
const FRANCHISE_SKIPPED_DOWNLOAD_COLUMNS = [
  "Franchise ID",
  "Reporting Period Start Year (YYYY)",
  "Reporting Period End Year (YYYY)",
  "Scope 1 Emissions (tCO2e)",
  "Scope 2 Emissions (tCO2e)",
  "Scope 3 Emissions (tCO2e)",
  "Remark",
];

const Franchises = () => {
  const { user } = useUser();
  const [sortConfig, setSortConfig] = useState({ key: "", direction: "" });
  const [tableData, setTableData] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editModal, setEditModal] = useState(false);
  const [viewModal, setViewModal] = useState(false);
  const [editData, setEditData] = useState({});
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [filterObj, setFilterObj] = useState({});
  const [deletedItemId, setDeletedItemId] = useState("");
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [bulkUploadModal, setBulkUploadModal] = useState(false);
  const [excelData, setExcelData] = useState(null);
  const [bulkUploadTable, setBulkUploadTable] = useState(false);
  const [formDataBulkUpload, setFormDataBulkUpload] = useState([]);
  const [matchData, setMatchData] = useState([]);
  const [skippedUploadDialog, setSkippedUploadDialog] = useState({
    open: false,
    skippedEntries: [],
    inserted: 0,
    skipped: 0,
  });
  const [masterFranchises, setMasterFranchises] = useState([]);
  const [franchiseSearchTerm, setFranchiseSearchTerm] = useState("");

  const [filters, setFilters] = useState({
    country: [],
    franchiseType: [],
  });

  const [cardData, setCardData] = useState({
    totalEntries: 0,
  });

  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const [snackOpen, setsnackOpen] = useState({
    open: false,
    message: "",
    severity: "",
  });

  const headers = [
    { key: "createdAt", label: "Date", sortable: true, width: "12%" },
    { key: "franchiseName", label: "Franchise Name", sortable: true, width: "22%" },
    { key: "country", label: "Country", sortable: true, width: "16%" },
    { key: "franchiseType", label: "Franchise Type", sortable: true, width: "20%" },
    { key: "reportingPeriod", label: "Reporting Period", sortable: false, width: "20%" },
  ];

  const inputData = [
    { fieldName: "franchiseId", title: "Franchise ID" },
    { fieldName: "franchiseName", title: "Franchise Name" },
    { fieldName: "country", title: "Country" },
    { fieldName: "locationAddress", title: "Location/Address" },
    { fieldName: "reportingStart", title: "Reporting Period Start Date" },
    { fieldName: "reportingEnd", title: "Reporting Period End Date" },
    { fieldName: "contactPerson", title: "Contact Person" },
    { fieldName: "franchiseType", title: "Franchise Type" },
    { fieldName: "remark", title: "Remark (optional)" },
    {
      fieldName: "scope1EmissionsMetricTon",
      title: "Scope 1 Emissions (tCO2e)",
      numericOnly: true,
    },
    {
      fieldName: "scope2EmissionsMetricTon",
      title: "Scope 2 Emissions (tCO2e)",
    },
    {
      fieldName: "scope3EmissionsMetricTon",
      title: "Scope 3 Emissions (tCO2e)",
    },
    { fieldName: "editRemarkReason", title: "Edit Remark Reason" },
  ];

  const filterData = [
    {
      title: "Date",
      type: "date",
      key: "date",
      value: [],
    },
    {
      title: "Reporting Period",
      type: "reportingYearRange",
      key: "reportingPeriod",
      value: [],
    },
    {
      title: "Country",
      type: "option",
      key: "country",
      value: filters?.country || [],
    },
    {
      title: "Franchise Type",
      type: "option",
      key: "franchiseType",
      value: filters?.franchiseType || [],
    },
  ];

  const fetchData = async () => {
    try {
      const filter = { ...filterObj };

      // Apply date range filter (createdAt / date) similar to other Scope 3 modules
      if (filter.date) {
        filter.startDate = filter?.date?.start;
        filter.endDate = filter?.date?.end || new Date();
        filter.dateField = "createdAt";
        delete filter.date;
      }
      if (filter.reportingPeriod) {
        const startYear = Number(filter?.reportingPeriod?.start);
        const endYear = Number(filter?.reportingPeriod?.end);
        if (!Number.isNaN(startYear)) {
          filter.reportingStart = `${startYear}-04-01`;
        }
        if (!Number.isNaN(endYear)) {
          filter.reportingEnd = `${endYear}-03-31`;
        }
        delete filter.reportingPeriod;
      }

      // Convert array filters to comma-separated strings for the backend
      // Remove empty arrays and undefined values
      Object.keys(filter).forEach((key) => {
        if (Array.isArray(filter[key])) {
          if (filter[key].length > 0) {
            filter[key] = filter[key].join(",");
          } else {
            delete filter[key];
          }
        } else if (filter[key] === undefined || filter[key] === null || filter[key] === "") {
          delete filter[key];
        }
      });

      const queryParams = new URLSearchParams({
        ...filter,
        page: currentPage,
        limit: 10,
        sortField: sortConfig.key,
        sortOrder: sortConfig.direction,
      }).toString();

      const res = await get(`/scope3/franchises/get?${queryParams}`);

      const arr =
        res?.data?.data?.map((item) => {
          const start = item?.reportingStart;
          const end = item?.reportingEnd;
          let reportingPeriod = "";

          if (start || end) {
            const startStr = start ? formatReportingDateGhgEntry(start) : "";
            const endStr = end ? formatReportingDateGhgEntry(end) : "";
            reportingPeriod = [startStr, endStr].filter(Boolean).join(" - ");
          }

          const m =
            item?.franchiseMaster && typeof item.franchiseMaster === "object"
              ? item.franchiseMaster
              : null;

          return {
            ...item,
            reportingPeriod,
            action: "View",
            id: item?._id,
            franchiseName: m?.franchiseName ?? item?.franchiseName,
            franchiseId: m?.franchiseId ?? item?.franchiseId,
            country: m?.country ?? item?.country,
            franchiseType: m?.franchiseType ?? item?.franchiseType,
            locationAddress: m?.locationAddress ?? item?.locationAddress,
            contactPerson: m?.contactPerson ?? item?.contactPerson,
          };
        }) || [];

      setTableData(arr);
      setTotalPages(res?.data?.totalPages || 1);
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    fetchData();
  }, [sortConfig, filterObj, currentPage]);

  const fetchCardData = async () => {
    try {
      const res = await get(`/scope3/franchises/cardData`);
      const data = res?.data?.data || {};
      setCardData({
        totalEntries: data?.totalEntries || 0,
      });
    } catch (error) {
      console.error(error);
    }
  };

  const fetchFilters = async () => {
    try {
      const res = await get(`/scope3/franchises/filter`);
      const { country, franchiseType } = res?.data?.data || {};

      setFilters({
        country: country || [],
        franchiseType: franchiseType || [],
      });
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    fetchCardData();
  }, []);

  useEffect(() => {
    fetchExcelData();
  }, []);

  useEffect(() => {
    fetchFilters();
  }, []);

  const refreshSummaryCards = async () => {
    await Promise.all([fetchCardData(), fetchFilters()]);
  };

  const uploadErrorClose = () => {
    setBulkUploadTable(true);
  };

  const handleCloseBulkUpload = () => {
    setBulkUploadModal(false);
    setBulkUploadTable(false);
    setMatchData([]);
    setFormDataBulkUpload([]);
    fetchData();
    refreshSummaryCards();
  };

  const getUniqueValueCount = (values = []) => {
    return new Set(
      values
        .map((value) => (typeof value === "string" ? value.trim() : value))
        .filter((value) => value !== undefined && value !== null && value !== "")
    ).size;
  };

  const fetchMasterFranchises = async (franchiseId = "") => {
    try {
      const queryParams = new URLSearchParams({
        page: "1",
        limit: "1000",
        sortField: "createdAt",
        sortOrder: "desc",
      });
      if (franchiseId?.trim()) {
        queryParams.set("franchiseId", franchiseId.trim());
      }
      const res = await get(`/master/franchises?${queryParams.toString()}`);
      setMasterFranchises(res?.data?.data || []);
    } catch (error) {
      console.error("Error fetching master franchises:", error);
      setMasterFranchises([]);
    }
  };

  useEffect(() => {
    const term = franchiseSearchTerm.trim();
    const shouldSearch = term.length === 0 || term.length >= 3;
    if (!shouldSearch) return;

    const timer = setTimeout(() => {
      fetchMasterFranchises(term);
    }, 3000);

    return () => clearTimeout(timer);
  }, [franchiseSearchTerm]);

  const bannerData = [
    {
      title: "Total Number of Franchises",
      description: `${cardData?.totalEntries ?? 0}`,
    },
    {
      title: "Total Countries with Active Franchises",
      description: `${getUniqueValueCount(filters?.country)}`,
    },
    {
      title: "Total Franchises Types",
      description: `${getUniqueValueCount(filters?.franchiseType)}`,
    }
  ];

  const handleSort = (key, direction) => {
    setSortConfig({ key, direction });
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const handleFilterChange = (newFilterObj) => {
    setFilterObj(newFilterObj);
    setCurrentPage(1);
  };

  const fetchExcelData = async () => {
    try {
      const res = await get(
        `/master/franchises?page=1&limit=1000&sortField=createdAt&sortOrder=desc`
      );
      const masters = Array.isArray(res?.data?.data) ? res.data.data : [];

      const template = masters.length
        ? masters.map((item) => ({
            "Franchise ID": item?.franchiseId || "",
            "Reporting Period Start Year (YYYY)": "",
            "Reporting Period End Year (YYYY)": "",
            "Scope 1 Emissions (tCO2e)": "",
            "Scope 2 Emissions (tCO2e)": "",
            "Scope 3 Emissions (tCO2e)": "",
            Remark: "",
          }))
        : [
            {
              "Franchise ID": "",
              "Reporting Period Start Year (YYYY)": "",
              "Reporting Period End Year (YYYY)": "",
              "Scope 1 Emissions (tCO2e)": "",
              "Scope 2 Emissions (tCO2e)": "",
              "Scope 3 Emissions (tCO2e)": "",
              Remark: "",
            },
          ];

      setExcelData(template);
    } catch (error) {
      console.error("Error generating franchises bulk upload template:", error);
      setExcelData([
        {
          "Franchise ID": "",
          "Reporting Period Start Year (YYYY)": "",
          "Reporting Period End Year (YYYY)": "",
          "Scope 1 Emissions (tCO2e)": "",
          "Scope 2 Emissions (tCO2e)": "",
          "Scope 3 Emissions (tCO2e)": "",
          Remark: "",
        },
      ]);
    }
  };

  const openModal = (type, dataForEdit) => {
    if (type === "add") {
      void fetchMasterFranchises("");
      setIsModalOpen(true);
      setEditModal(false);
      setEditData({});
    } else if (type === "edit") {
      void fetchMasterFranchises("");
      setIsModalOpen(true);
      setEditData(dataForEdit);
      setEditModal(true);
      setViewModal(false);
    }
  };

  const handleEdit = (data) => {
    openModal("edit", data);
  };

  const openViewModal = (data) => {
    setEditData(data);
    setViewModal(true);
  };

  const closeModal = (type) => {
    if (type === "add") {
      setIsModalOpen(false);
    } else if (type === "edit") {
      setIsModalOpen(false);
      setEditModal(false);
      setEditData({});
    }
  };

  const handleSubmit = async (data, isEditing) => {
    try {
      const url = isEditing
        ? `/scope3/franchises/update?Id=${editData.id || editData._id}`
        : `/scope3/franchises/add`;

      const apiCall = isEditing ? put : post;

      const payload = {
        ...data,
        reportingStart: data.reportingPeriodStart,
        reportingEnd: data.reportingPeriodEnd,
      };
      delete payload.reportingPeriodStart;
      delete payload.reportingPeriodEnd;

      const franchiseFieldsFromMasterOnly = [
        "franchiseName",
        "franchiseId",
        "country",
        "locationAddress",
        "contactPerson",
        "franchiseType",
      ];
      franchiseFieldsFromMasterOnly.forEach((key) => {
        delete payload[key];
      });

      const res = await apiCall(url, payload);

      if (res) {
        setsnackOpen({
          open: true,
          message: res?.data?.message,
          severity: "success",
        });

        setIsModalOpen(false);

        if (isEditing) {
          setEditModal(false);
          setEditData({});
        }

        refreshSummaryCards();
        fetchData();
      }
    } catch (error) {
      setsnackOpen({
        open: true,
        message: error?.response?.data?.message || "Something went wrong",
        severity: "error",
      });

      console.error("Error in handleSubmit (Franchises):", error);
    }
  };

  const handleDeleteOpen = (row) => {
    setDeletedItemId(row);
    setDeleteDialog(true);
  };



  return (
    <>
      <Cards data={bannerData} title={"Franchises"} />

      <ActionButtons
        onFilterClick={() => setIsFilterModalOpen(true)}
        onAddClick={() => openModal("add")}
        filterObj={filterObj}
        onUploadClick={() => setBulkUploadModal(true)}
      />

      <ViewTable
        title={"Franchises"}
        editIcon={user?.companyAdmin ? "disable" : "enable"}
        headers={headers}
        data={tableData ?? []}
        onSort={handleSort}
        sortConfig={sortConfig}
        page={currentPage}
        count={10}
        totalPages={totalPages}
        onPageChange={handlePageChange}
        actionColumnWidthPercent={10}
        onView={(row) => {
          openViewModal(row);
        }}
        onEdit={(row) => openModal("edit", row)}
        onDelete={(row) => handleDeleteOpen(row)}
      />

      <Snackbar
        open={snackOpen.open}
        autoHideDuration={3000}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        onClose={() => setsnackOpen({ ...snackOpen, open: false })}
      >
        <Alert
          onClose={() => setsnackOpen({ ...snackOpen, open: false })}
          severity={snackOpen.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackOpen.message}
        </Alert>
      </Snackbar>

      <Form
        isOpen={isModalOpen}
        onClose={() => closeModal(editModal ? "edit" : "add")}
        onSubmit={handleSubmit}
        header={editModal ? "Edit Franchise" : "Add Franchise"}
        initialData={editData}
        isEditing={editModal}
        franchiseOptions={masterFranchises}
        onSearchFranchises={setFranchiseSearchTerm}
      />

      <ViewData
        id={editData ? editData.id || editData._id : ""}
        element={editData}
        title={"Franchises"}
        viewData={viewModal}
        setViewData={setViewModal}
        setData={setEditData}
        inputData={inputData}
        handleEdit={handleEdit}
        companyAdmin={user?.companyAdmin}
      />

      {deleteDialog && (
        <DeleteAlertDialog
          {...{
            deleteDialog,
            setDeleteDialog,
            method: "delete",
            id: deletedItemId?._id,
            url: `${api}/scope3/franchises/delete`,
            setsnackOpen,
            fetchData,
            fetchCardData: refreshSummaryCards,
          }}
        />
      )}

      <FilterModal
        isOpen={isFilterModalOpen}
        onClose={() => setIsFilterModalOpen(false)}
        filters={filterData}
        onFilterChange={handleFilterChange}
        title="Filter"
      />

      {bulkUploadModal && (
        <BulkUploadModal
          onCloseBulkUpload={handleCloseBulkUpload}
          setMatchData={setMatchData}
          setFormDataBulkUpload={setFormDataBulkUpload}
          matchedKey="franchiseStatusList"
          errorClose={uploadErrorClose}
          setBulkUploadModal={setBulkUploadModal}
          isBulkUploadModal={bulkUploadModal}
          excelData={excelData}
          fileName={"franchises"}
          setsnackOpen={setsnackOpen}
          url={"/scope3/franchises/bulkupload"}
        />
      )}

      {bulkUploadTable && (
        <BulkUploadReview
          bulkUploadTable={bulkUploadTable}
          matchData={matchData}
          setMatchData={setMatchData}
          formDataBulkUpload={formDataBulkUpload}
          setFormDataBulkUpload={setFormDataBulkUpload}
          setsnackOpen={setsnackOpen}
          bulkUploadUrl={"/scope3/franchises/jsonupload"}
          onClose={() => setBulkUploadTable(false)}
          onConfirm={(data) => {
            setBulkUploadTable(false);
            setMatchData([]);
            setFormDataBulkUpload([]);
            fetchData();
            refreshSummaryCards();
            void fetchMasterFranchises("");
            void fetchExcelData();

            const skippedEntries = data?.skippedEntries;
            const hasSkipped =
              Array.isArray(skippedEntries) && skippedEntries.length > 0;
            const inserted = Number(data?.inserted ?? 0);
            const skipped = Number(data?.skipped ?? 0);

            if (hasSkipped) {
              setSkippedUploadDialog({
                open: true,
                skippedEntries,
                inserted,
                skipped,
              });
            }
          }}
        />
      )}

      <BulkUploadSkippedDialog
        open={skippedUploadDialog.open}
        skippedEntries={skippedUploadDialog.skippedEntries}
        inserted={skippedUploadDialog.inserted}
        skipped={skippedUploadDialog.skipped}
        downloadFileName="franchises_skipped"
        downloadExcelColumns={FRANCHISE_SKIPPED_DOWNLOAD_COLUMNS}
        onClose={() =>
          setSkippedUploadDialog({
            open: false,
            skippedEntries: [],
            inserted: 0,
            skipped: 0,
          })
        }
        // onDownload={(rows) => setExcelData(rows)}
      />
    </>
  );
};

export default Franchises;

